import { get, isEmpty, isNil, isNumber, isObject } from 'lodash';
// Import Types
import type { NestedArrayJsonType, NestedJsonType, SMSProviderFormFields, SMSProviderResponseDTO } from './types';
// Import Enums
import { PageTypesEnum } from 'enums';
// Import Constants
import { SMS_PROVIDER_FORM_DEFAULT_VALUES, SMSProviderFieldsEnum } from './constants';

type ValueConstructorType = NumberConstructor | StringConstructor;

export const mapApiModelToFormData = (values: SMSProviderResponseDTO, mode: PageTypesEnum): SMSProviderFormFields => {
    const {
        Note,
        PartnerId,
        Password,
        MediaType,
        PostMessageBody,
        MaxDegreeOfParallelism,
        DelayTimeout,
        Url,
        ApiKey,
        Name,
        Description,
        UserName,
        FromAddress,
        RequestType,

        ResponseRegex: ResponseRegexes,
        AdditionalFields,
    } = values;

    const jsonParseNestedFields = (values: Record<string, string> | string | number) => {
        if (!isObject(values)) return values;

        return Object.fromEntries(
            Object.entries(values).map(([key, value]) => {
                try {
                    const newValue = JSON.parse(value);

                    if (isNumber(newValue)) {
                        throw new Error('');
                    }

                    return [key, newValue];
                } catch {
                    return [key, value];
                }
            }),
        );
    };

    const recursiveParseJSONModel = ({
        values,
        valueConstructor,
        dept = Infinity,
    }: {
        values?: NestedJsonType | string | number | null;
        valueConstructor?: ValueConstructorType;
        dept?: number;
    }) => {
        if (isNil(values)) return [];

        const parsedValues = jsonParseNestedFields(values);

        if (isObject(parsedValues)) {
            return Object.entries(parsedValues).map(([key, value]) => {
                if (isNil(value)) {
                    return [key, ''];
                }
                if (isObject(value) && dept > 0) {
                    return [key, recursiveParseJSONModel({ values: value, valueConstructor, dept: dept - 1 })];
                }
                if (valueConstructor) {
                    const newValue = valueConstructor(value);
                    if (isNumber(newValue) && isNaN(newValue)) {
                        return [key, ''];
                    }
                    return [key, newValue];
                }
                return [key, value];
            });
        }

        if (valueConstructor) {
            const newValue = valueConstructor(parsedValues);
            if (isNumber(newValue) && isNaN(newValue)) {
                return null;
            }
            return newValue;
        }

        return parsedValues;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const lowerCaseFirstLetterOfObjectKeys = (obj: Record<string, any> | null) => {
        if (isNil(obj)) return {};
        return Object.fromEntries(
            Object.entries(obj).map(([key, value]) => [key.slice(0, 1).toLowerCase() + key.slice(1), value]),
        );
    };

    const { Type, Properties, Error: ErrorProperties, SuccessCode, DefaultCode, CodeMap } = ResponseRegexes;

    const getValuesFromDefaultIfNotExists = <T>(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        values: Record<string, any>,
        valueConstructor?: StringConstructor | NumberConstructor,
    ): T => {
        return Object.fromEntries(
            Object.entries(values).map(([key, value]) => {
                if (isNil(value)) {
                    return [key, get(SMS_PROVIDER_FORM_DEFAULT_VALUES, key)];
                }
                return [key, valueConstructor ? valueConstructor(value) : value];
            }),
        ) as T;
    };

    const gerResponseRegexesPropertiesData = (obj?: Record<string, unknown> | null) => {
        if (isNil(obj)) return {};

        return Object.fromEntries(
            Object.entries(obj).map(([key, value]) => {
                let newValue = String(value);

                if (!newValue) {
                    newValue = '';
                }

                return [key, newValue];
            }),
        );
    };

    const getNumberValue = (value?: string | number | null) => {
        if (isNil(value) || isNaN(+value)) return null;
        return +value;
    };

    return {
        ...getValuesFromDefaultIfNotExists({
            [SMSProviderFieldsEnum.Url]: Url,
            [SMSProviderFieldsEnum.ApiKey]: ApiKey,
            [SMSProviderFieldsEnum.Description]: Description,
            [SMSProviderFieldsEnum.UserName]: UserName,
            [SMSProviderFieldsEnum.Password]: Password,
            [SMSProviderFieldsEnum.FromAddress]: FromAddress,
            [SMSProviderFieldsEnum.MediaType]: MediaType,
            [SMSProviderFieldsEnum.PostMessageBody]: PostMessageBody,
            [SMSProviderFieldsEnum.Note]: Note,
        }),
        ...getValuesFromDefaultIfNotExists(
            {
                [SMSProviderFieldsEnum.PartnerId]: PartnerId,
                [SMSProviderFieldsEnum.RequestType]: RequestType,
                [SMSProviderFieldsEnum.MaxDegreeOfParallelism]: MaxDegreeOfParallelism,
                [SMSProviderFieldsEnum.DelayTimeout]: DelayTimeout,
            },
            Number,
        ),

        [SMSProviderFieldsEnum.Name]: Name
            ? mode === PageTypesEnum.clone
                ? `${Name} (2)`
                : Name
            : SMS_PROVIDER_FORM_DEFAULT_VALUES[SMSProviderFieldsEnum.Name],

        [SMSProviderFieldsEnum.ResponseRegex]: {
            [SMSProviderFieldsEnum.Type]: getNumberValue(Type),

            [SMSProviderFieldsEnum.Properties]: {
                ...SMS_PROVIDER_FORM_DEFAULT_VALUES[SMSProviderFieldsEnum.ResponseRegex][
                    SMSProviderFieldsEnum.Properties
                ],
                ...lowerCaseFirstLetterOfObjectKeys(gerResponseRegexesPropertiesData(Properties)),
            },

            [SMSProviderFieldsEnum.Error]: {
                ...SMS_PROVIDER_FORM_DEFAULT_VALUES[SMSProviderFieldsEnum.ResponseRegex][SMSProviderFieldsEnum.Error],
                ...lowerCaseFirstLetterOfObjectKeys(gerResponseRegexesPropertiesData(ErrorProperties)),
            },

            [SMSProviderFieldsEnum.SuccessCode]: getNumberValue(SuccessCode),

            [SMSProviderFieldsEnum.DefaultCode]: getNumberValue(DefaultCode),

            [SMSProviderFieldsEnum.CodeMap]: [
                ...SMS_PROVIDER_FORM_DEFAULT_VALUES[SMSProviderFieldsEnum.ResponseRegex][SMSProviderFieldsEnum.CodeMap],
                ...recursiveParseJSONModel({ values: CodeMap, valueConstructor: Number }),
            ],
        },
        [SMSProviderFieldsEnum.AdditionalFields]: [
            ...SMS_PROVIDER_FORM_DEFAULT_VALUES[SMSProviderFieldsEnum.AdditionalFields],
            ...recursiveParseJSONModel({ values: AdditionalFields, valueConstructor: String }),
        ],
    };
};

export const mapFormDataToApiModel = (values: SMSProviderFormFields) => {
    const {
        ApiKey,
        DelayTimeout,
        Description,
        FromAddress,
        MaxDegreeOfParallelism,
        MediaType,
        Name,
        Note,
        PartnerId,
        Password,
        PostMessageBody,
        RequestType,
        Url,
        UserName,
        ResponseRegex: ResponseRegexes,
        AdditionalFields,
    } = values;

    const transformEmptyValues = (value: string | number | null, valueConstructor: ValueConstructorType = String) => {
        if (isNumber(value) ? isNaN(value) : isEmpty(value)) return null;

        return valueConstructor ? valueConstructor(value) : value;
    };

    const convertDynamicNestedFieldsToJSONModel = (
        values: NestedArrayJsonType[],
        valueConstructor: ValueConstructorType = String,
    ) => {
        if (!isObject(values)) return values || {};

        return values.reduce((acc, [key, value]) => {
            if (Array.isArray(value)) {
                acc[key as string] = convertDynamicNestedFieldsToJSONModel(
                    value as NestedArrayJsonType[],
                    valueConstructor,
                );
            } else {
                acc[key as string] = transformEmptyValues(value, valueConstructor);
            }

            return acc;
        }, {});
    };

    const removeEmptyValuesFromObject = (obj: Record<string, unknown>) => {
        return Object.fromEntries(
            Object.entries(obj).filter(([_key, value]) => (isNumber(value) ? !isNaN(value) : !isEmpty(value))),
        );
    };

    const jsonStringifyNestedFields = (obj: Record<string, unknown>) => {
        return Object.fromEntries(
            Object.entries(obj).map(([key, value]) => [key, isObject(value) ? JSON.stringify(value) : value]),
        );
    };

    const { Type, Properties, Error, CodeMap, DefaultCode, SuccessCode } = ResponseRegexes;

    return {
        [SMSProviderFieldsEnum.PartnerId]: transformEmptyValues(PartnerId, Number),
        [SMSProviderFieldsEnum.Url]: transformEmptyValues(Url),
        [SMSProviderFieldsEnum.ApiKey]: transformEmptyValues(ApiKey),
        [SMSProviderFieldsEnum.Name]: transformEmptyValues(Name),
        [SMSProviderFieldsEnum.Description]: transformEmptyValues(Description),
        [SMSProviderFieldsEnum.UserName]: transformEmptyValues(UserName),
        [SMSProviderFieldsEnum.Password]: transformEmptyValues(Password),
        [SMSProviderFieldsEnum.FromAddress]: transformEmptyValues(FromAddress),
        [SMSProviderFieldsEnum.RequestType]: transformEmptyValues(RequestType, Number),
        [SMSProviderFieldsEnum.MediaType]: transformEmptyValues(MediaType),
        [SMSProviderFieldsEnum.PostMessageBody]: transformEmptyValues(PostMessageBody),
        [SMSProviderFieldsEnum.MaxDegreeOfParallelism]: transformEmptyValues(MaxDegreeOfParallelism, Number),
        [SMSProviderFieldsEnum.DelayTimeout]: transformEmptyValues(DelayTimeout, Number),
        [SMSProviderFieldsEnum.Note]: transformEmptyValues(Note),

        [SMSProviderFieldsEnum.ResponseRegex]: removeEmptyValuesFromObject({
            [SMSProviderFieldsEnum.Type]: transformEmptyValues(Type, Number),

            [SMSProviderFieldsEnum.Properties]: removeEmptyValuesFromObject({
                [SMSProviderFieldsEnum.ReplaceBy]: transformEmptyValues(Properties?.replaceBy),
                [SMSProviderFieldsEnum.ErrorCode]: transformEmptyValues(Properties?.errorCode),
                [SMSProviderFieldsEnum.ErrorMessage]: transformEmptyValues(Properties?.errorMessage),
                [SMSProviderFieldsEnum.ExternalId]: transformEmptyValues(Properties?.externalId),
                [SMSProviderFieldsEnum.DeliveryStatus]: transformEmptyValues(Properties?.deliveryStatus),
            }),
            [SMSProviderFieldsEnum.Error]: removeEmptyValuesFromObject({
                [SMSProviderFieldsEnum.ReplaceBy]: transformEmptyValues(Error?.replaceBy),
                [SMSProviderFieldsEnum.ErrorCode]: transformEmptyValues(Error?.errorCode),
                [SMSProviderFieldsEnum.ErrorMessage]: transformEmptyValues(Error?.errorMessage),
                [SMSProviderFieldsEnum.ExternalId]: transformEmptyValues(Error?.externalId),
                [SMSProviderFieldsEnum.DeliveryStatus]: transformEmptyValues(Error?.deliveryStatus),
            }),

            [SMSProviderFieldsEnum.DefaultCode]: transformEmptyValues(DefaultCode, Number),
            [SMSProviderFieldsEnum.SuccessCode]: transformEmptyValues(SuccessCode, Number),

            [SMSProviderFieldsEnum.CodeMap]: convertDynamicNestedFieldsToJSONModel(CodeMap, Number),
        }),

        [SMSProviderFieldsEnum.AdditionalFields]: jsonStringifyNestedFields(
            convertDynamicNestedFieldsToJSONModel(AdditionalFields),
        ),
    };
};
